import { useState, useEffect, useContext, useRef, memo, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaRandom, FaPlay, FaEye, FaYoutube, FaFilter, FaCheckCircle, FaTimes, FaSort, FaStar } from 'react-icons/fa';
import api from '../utils/api';
import { ThemeContext } from '../context/ThemeContext';
import { Tag, Button, Input } from '../components/ui';
import { getProblemUrl } from '../utils/problemUtils';
import { useAuth } from '../contexts/AuthContext';
import ProblemPreviewModal from '../components/problems/ProblemPreviewModal';
import MyListsSidebar from '../components/lists/MyListsSidebar';
import { fetchUserLists, addProblemsToList, removeProblemFromList, toggleFavoriteProblem } from '../utils/listsApi';

// Lazy load skeleton for faster initial load
const ProblemListSkeleton = lazy(() => 
  import('../components/skeletons').then(module => ({ default: module.ProblemListSkeleton }))
);

// Memoized Problem Row Component for better performance
const ProblemRow = memo(({ problem, index, onNavigate, onPreview, onTagClick, getDifficultyBadge, favoriteListId, onToggleFavorite }) => {
  const inFav = problem.isInFavorite === true;
  const showStar = !!favoriteListId;
  return (
    <div className="grid grid-cols-12 gap-4 p-4 border-b last:border-b-0 border-border-primary hover:bg-background-secondary transition-colors bg-background-primary">
      <div className="col-span-1 text-center flex items-center justify-center text-text-secondary">
        {(problem.isSolved || problem.solved) && <FaCheckCircle className="text-green-500 mr-1" size={12} />}
        {problem.questionNumber || (index + 1)}
      </div>
      <div className="col-span-5 flex flex-col">
        <button
          className="text-left hover:text-text-accent font-medium flex items-center gap-2"
          onClick={() => onNavigate(problem)}
        >
          {problem.title}
          {problem.isPremium && (
            <span className="px-1.5 py-0.5 rounded text-[10px] bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
              Premium
            </span>
          )}
        </button>
        {Array.isArray(problem.tags) && problem.tags.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {problem.tags.slice(0, 6).map(tag => (
              <Tag
                key={`${problem._id}-${tag}`}
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onTagClick(tag);
                }}
              >
                {tag}
              </Tag>
            ))}
          </div>
        )}
      </div>
      <div className="col-span-2 flex items-center justify-center">
        {getDifficultyBadge(problem.difficulty)}
      </div>
      <div className="col-span-2 text-center flex items-center justify-center">
        <span className="text-sm text-text-secondary">
          {problem.acceptanceRate || Math.floor(Math.random() * 100)}%
        </span>
      </div>
      <div className="col-span-2 flex items-center justify-center space-x-2">
        {showStar && (
          <button
            className={`p-2 rounded-full flex-shrink-0 ${inFav ? 'text-yellow-500' : 'text-text-tertiary hover:text-yellow-500 hover:bg-yellow-500/10'}`}
            onClick={(e) => { e.stopPropagation(); onToggleFavorite(problem._id || problem.id, inFav); }}
            title={inFav ? 'Remove from Favorites' : 'Add to Favorites'}
          >
            <FaStar size={14} className={inFav ? 'fill-current' : ''} />
          </button>
        )}
        <button
          className="p-2 rounded-full hover:bg-button-primary-bg hover:bg-opacity-10 text-button-primary-bg"
          onClick={() => onNavigate(problem)}
          title="Practice"
        >
          <FaPlay />
        </button>
        <button
          className="p-2 rounded-full hover:bg-button-secondary-bg hover:bg-opacity-10 text-text-secondary"
          onClick={(e) => {
            e.stopPropagation();
            onPreview(problem);
          }}
          title="Preview"
        >
          <FaEye />
        </button>
        <button
          className="p-2 rounded-full hover:bg-red-500 hover:bg-opacity-10 text-red-500"
          onClick={() => window.open(`https://www.youtube.com/results?search_query=${encodeURIComponent(problem.title + ' solution')}`, '_blank')}
          title="Video Solution"
        >
          <FaYoutube />
        </button>
      </div>
    </div>
  );
});

ProblemRow.displayName = 'ProblemRow';

const Problems = () => {
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const { currentUser } = useAuth();
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [, setTotalPages] = useState(1);
  const [totalProblems, setTotalProblems] = useState(3);
  const [solvedProblems, setSolvedProblems] = useState(1);
  const [unsolvedProblems, setUnsolvedProblems] = useState(2);
  const [successRate, setSuccessRate] = useState(33);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [problemsPerPage] = useState(20);
  const [favoriteListId, setFavoriteListId] = useState(null);
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [activeTags, setActiveTags] = useState([]);
  const [randomDifficulty, setRandomDifficulty] = useState('any');
  const [sortOrder, setSortOrder] = useState('question-asc');
  const [hasMore, setHasMore] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [previewProblem, setPreviewProblem] = useState(null);

  // Refs for 100% reliable infinite scroll
  const scrollContainerRef = useRef(null);
  const isLoadingMoreRef = useRef(false);
  const hasMoreRef = useRef(true);
  const isMountedRef = useRef(false);
  const prevFiltersRef = useRef({ searchTerm: '', difficultyFilter: 'all', categoryFilter: 'all', activeTags: [], sortOrder: 'question-asc' });

  // Clear cache and set mounted flag
  useEffect(() => {
    sessionStorage.clear();
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Listen for progress updates (when problems are solved)
  useEffect(() => {
    const handleProgressUpdate = () => {
      sessionStorage.clear();
      setCurrentPage(1);
      setProblems([]);
      setHasMore(true);
      setLoading(true);
      setTimeout(() => {
        setSearchTerm(prev => prev);
      }, 100);
    };
    
    window.addEventListener('codebattle:progress:update', handleProgressUpdate);
    
    const storageHandler = (e) => {
      if (e.key === 'codebattle:progress:lastUpdate') {
        handleProgressUpdate();
      }
    };
    window.addEventListener('storage', storageHandler);

    return () => {
      window.removeEventListener('codebattle:progress:update', handleProgressUpdate);
      window.removeEventListener('storage', storageHandler);
    };
  }, []);

  // Sync hasMore to ref for scroll handler
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  // Prefetch next page when user scrolls near bottom
  useEffect(() => {
    if (hasMore && !loading && !loadingMore && problems.length > 0) {
      const nextPage = currentPage + 1;
      // Prefetch next page in background
      api.get('/api/problems', {
        params: {
          page: nextPage,
          limit: problemsPerPage,
          search: searchTerm || undefined,
          difficulty: difficultyFilter !== 'all' ? difficultyFilter : undefined,
          category: categoryFilter !== 'all' ? categoryFilter : undefined,
          tags: activeTags.length ? activeTags.join(',') : undefined,
          sort: sortOrder
        }
      }).catch(() => {}); // Silently fail prefetch
    }
  }, [currentPage, hasMore, loading, loadingMore, problems.length, searchTerm, difficultyFilter, categoryFilter, activeTags, sortOrder, problemsPerPage]);

  // Fetch problems from API (server-side pagination with infinite scroll)
  useEffect(() => {
    // Set tab title and favicon for Problems
    document.title = 'CodeBattles - Coding Challenge | Problems';
    const link = document.querySelector("link[rel='icon']");
    if (link) { link.setAttribute('href', '/codebattles-logo-small.png'); }

    /**
    * Fetches and updates problem list, pagination, and stats, handling loading states and errors.
    * @example
    * sync()
    * undefined
    * @param {{void}} {{none}} - No arguments required.
    * @returns {{Promise<void>}} Promise resolving once all state updates and side effects complete.
    **/
    const fetchProblems = async () => {
      if (!isMountedRef.current) return; // Don't fetch if not mounted

      const isLoadMore = isLoadingMoreRef.current;

      try {
        if (isLoadMore) {
          setLoadingMore(true);
        } else {
          setLoading(true);
          setProblems([]);
        }

        const response = await api.get('/api/problems', {
          params: {
            page: currentPage,
            limit: problemsPerPage,
            search: searchTerm || undefined,
            difficulty: difficultyFilter !== 'all' ? difficultyFilter : undefined,
            category: categoryFilter !== 'all' ? categoryFilter : undefined,
            tags: activeTags.length ? activeTags.join(',') : undefined,
            sort: sortOrder
          }
        });

        let fetchedProblems = (response.data?.problems || []).filter(p => p._id && p.title && !/demo|test/i.test(p.title));
        
        // Fetch solved problems from backend (only once, not on every page load)
        if (currentPage === 1 && !isLoadMore) {
          try {
            const { fetchSolvedProblems } = await import('../utils/submissionsApi');
            await fetchSolvedProblems();
          } catch (err) {
            // Ignore errors
          }
        }

        if (isLoadMore) {
          setProblems(prev => [...prev, ...fetchedProblems]);
        } else {
          setProblems(fetchedProblems);
        }

        const totalPgs = response.data?.totalPages || 1;
        const totalProbs = response.data?.total || fetchedProblems.length;
        const solvedCount = fetchedProblems.filter(p => p.isSolved || p.solved).length;

        setTotalPages(totalPgs);
        setTotalProblems(totalProbs);
        setSolvedProblems(solvedCount);
        setUnsolvedProblems(totalProbs - solvedCount);
        setSuccessRate(totalProbs > 0 ? Math.round((solvedCount / totalProbs) * 100) : 0);
        setHasMore(currentPage < totalPgs);

        if (isLoadMore) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      } catch (err) {
        
        setError('Failed to fetch problems');
        setLoading(false);
        setLoadingMore(false);
        if (!isLoadMore) {
          setProblems([]);
        }
      } finally {
        // ✅ Always reset flag in finally block
        isLoadingMoreRef.current = false;
      }
    };

    // Check if filters changed (not just page)
    const filtersChanged = 
      prevFiltersRef.current.searchTerm !== searchTerm ||
      prevFiltersRef.current.difficultyFilter !== difficultyFilter ||
      prevFiltersRef.current.categoryFilter !== categoryFilter ||
      prevFiltersRef.current.activeTags.join(',') !== activeTags.join(',') ||
      prevFiltersRef.current.sortOrder !== sortOrder;

    if (filtersChanged && currentPage !== 1) {
      // Filters changed, reset to page 1
      prevFiltersRef.current = { searchTerm, difficultyFilter, categoryFilter, activeTags, sortOrder };
      isLoadingMoreRef.current = false;
      setCurrentPage(1);
      setProblems([]);
      setHasMore(true);
      return; // Don't fetch yet, let page change trigger it
    }

    // Update prev filters
    prevFiltersRef.current = { searchTerm, difficultyFilter, categoryFilter, activeTags, sortOrder };

    fetchProblems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, difficultyFilter, categoryFilter, activeTags.join(','), sortOrder]); // Re-fetch when filters change

  // ⚙️ 100% Reliable Container Scroll Detection
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

      // Trigger when within 100px of bottom (more aggressive)
      if (distanceFromBottom < 100 && hasMoreRef.current && !isLoadingMoreRef.current) {
        isLoadingMoreRef.current = true;
        setCurrentPage(prev => prev + 1);
      }
    };

    container.addEventListener('scroll', handleScroll);

    return () => container.removeEventListener('scroll', handleScroll);
  }, []); // Empty dependency - listener set once

  // Resolve Favorite list id for star (add to favorites)
  useEffect(() => {
    if (!currentUser) { setFavoriteListId(null); return; }
    fetchUserLists()
      .then((lists) => {
        const fav = (lists || []).find(l => l.isDefault);
        setFavoriteListId(fav ? fav.id : null);
      })
      .catch(() => setFavoriteListId(null));
  }, [currentUser]);

  // Debounced search for better performance
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchInput.trim() !== searchTerm) {
        setSearchTerm(searchInput.trim());
      }
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(debounceTimer);
  }, [searchInput, searchTerm]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput.trim());
  };

  /**
  * Renders a difficulty badge with contextual colors based on the provided level.
  * @example
  * renderDifficultyTag('easy')
  * <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:bg-opacity-20 dark:text-green-300">easy</span>
  * @param {{string}} {{difficulty}} - Difficulty level used to determine the badge styling.
  * @returns {{JSX.Element}} Styled span element representing the difficulty.
  **/
  const getDifficultyBadge = (difficulty) => {
    let colorClass = '';
    switch (difficulty?.toLowerCase()) {
      case 'easy':
        colorClass = 'bg-green-100 text-green-800 dark:bg-green-900 dark:bg-opacity-20 dark:text-green-300';
        break;
      case 'medium':
        colorClass = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:bg-opacity-20 dark:text-yellow-300';
        break;
      case 'hard':
        colorClass = 'bg-red-100 text-red-800 dark:bg-red-900 dark:bg-opacity-20 dark:text-red-300';
        break;
      default:
        colorClass = 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:bg-opacity-20 dark:text-gray-300';
    }
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>{difficulty || 'Unknown'}</span>;
  };

  const navigateToProblem = useRef((problem) => {
    // Accept either problem object or problem ID for backward compatibility
    if (typeof problem === 'string') {
      navigate(`/problems/${problem}`);
    } else {
      const url = getProblemUrl(problem);
      navigate(url);
    }
  }).current;

  const handleRandomProblem = useRef(() => {
    if (problems.length > 0) {
      const randomIndex = Math.floor(Math.random() * problems.length);
      const randomProblem = problems[randomIndex];
      const url = getProblemUrl(randomProblem);
      navigate(url);
    }
  }).current;

  const handleTagClick = useRef((tag) => {
    setActiveTags(prev => prev.includes(tag) ? prev : [...prev, tag]);
  }).current;

  const handlePreview = useRef((problem) => {
    setPreviewProblem(problem);
  }).current;

  const handleToggleFavorite = async (problemId, currentlyInFavorite) => {
    await toggleFavoriteProblem(favoriteListId, problemId, currentlyInFavorite, setProblems, 'isInFavorite');
  };

  return (
    <div className="min-h-screen flex flex-col transition-colors" style={{ backgroundColor: theme.background.primary, color: theme.text.primary }}>
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
        <div className="container mx-auto px-4 py-8 max-w-full">
          <div className="flex flex-col lg:flex-row gap-6 max-w-full">
            {/* Left sidebar (keep Progress/Favorites only; remove duplicate Filters) */}
            <div className="w-full lg:w-64 space-y-6">
              {/* Stats Card */}
              <div className="card p-4 space-y-3">
                <h2 className="text-lg font-bold">Your Progress</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-text-accent">{solvedProblems}</div>
                    <div className="text-xs text-text-secondary">Solved</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-text-tertiary">{unsolvedProblems}</div>
                    <div className="text-xs text-text-secondary">Unsolved</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-text-primary">{totalProblems}</div>
                    <div className="text-xs text-text-secondary">Total</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-500 dark:text-green-400">{successRate}%</div>
                    <div className="text-xs text-text-secondary">Success Rate</div>
                  </div>
                </div>
              </div>

              {/* Filters Card */}
              <div className="card p-4 space-y-3">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <FaFilter className="text-blue-400" />
                  Filters
                </h2>

                {/* Difficulty Filter */}
                <div>
                  <label className="text-xs text-text-secondary mb-1 block">Difficulty</label>
                  <select
                    value={difficultyFilter}
                    onChange={(e) => setDifficultyFilter(e.target.value)}
                    className="w-full px-3 py-2 rounded-md bg-background-tertiary dark:bg-background-secondary border border-border-primary text-text-primary text-sm focus:ring-2 focus:ring-button-primary-bg focus:outline-none"
                  >
                    <option value="all">All Difficulties</option>
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>

                {/* Category Filter */}
                <div>
                  <label className="text-xs text-text-secondary mb-1 block">Category</label>
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="w-full px-3 py-2 rounded-md bg-background-tertiary dark:bg-background-secondary border border-border-primary text-text-primary text-sm focus:ring-2 focus:ring-button-primary-bg focus:outline-none"
                  >
                    <option value="all">All Categories</option>
                    <option value="array">Array</option>
                    <option value="string">String</option>
                    <option value="tree">Tree</option>
                    <option value="graph">Graph</option>
                    <option value="dp">Dynamic Programming</option>
                    <option value="math">Math</option>
                  </select>
                </div>

                {/* Sort Order */}
                <div>
                  <label className="text-xs text-text-secondary mb-1 block">Sort By</label>
                  <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    className="w-full px-3 py-2 rounded-md bg-background-tertiary dark:bg-background-secondary border border-border-primary text-text-primary text-sm focus:ring-2 focus:ring-button-primary-bg focus:outline-none"
                  >
                    <option value="question-asc">Question Number: 1 → N</option>
                    <option value="question-desc">Question Number: N → 1</option>
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="difficulty-asc">Difficulty: Easy → Hard</option>
                    <option value="difficulty-desc">Difficulty: Hard → Easy</option>
                    <option value="acceptance-asc">Acceptance: Low → High</option>
                    <option value="acceptance-desc">Acceptance: High → Low</option>
                  </select>
                </div>

                {/* Clear Filters */}
                {(difficultyFilter !== 'all' || categoryFilter !== 'all' || sortOrder !== 'question-asc' || activeTags.length > 0 || searchTerm) && (
                  <Button
                    variant="danger"
                    onClick={() => {
                      setDifficultyFilter('all');
                      setCategoryFilter('all');
                      setSortOrder('question-asc');
                      setActiveTags([]);
                      setSearchTerm('');
                      setSearchInput('');
                    }}
                    className="w-full flex items-center justify-center gap-2"
                  >
                    <FaTimes />
                    Clear All Filters
                  </Button>
                )}
              </div>

              {/* My Lists - shared sidebar component */}
              <MyListsSidebar />
            </div>

            {/* Main content */}
            <div className="flex-1 min-w-0">
              {/* Top controls: tags rail, then compact search + category + random actions */}
              <div className="flex flex-col gap-3 mb-4 max-w-full">
                {/* Tags rail - horizontal scrollable in one line */}
                <div className="w-full overflow-x-auto overflow-y-hidden -mx-2 px-2">
                  <div className="flex items-center gap-2 py-2 w-max">
                    {Array.from(new Set(problems.flatMap(p => p.tags || []))).slice(0, 30).map(tag => (
                      <button
                        key={`rail-${tag}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 flex-shrink-0 ${activeTags.includes(tag)
                          ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg scale-105'
                          : 'bg-gray-700 text-gray-200 hover:bg-gradient-to-r hover:from-blue-500 hover:to-purple-600 hover:text-white hover:shadow-md dark:bg-gray-700 dark:text-gray-300 dark:hover:from-blue-500 dark:hover:to-purple-600'
                          }`}
                        onClick={() => {
                          setActiveTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
                        }}
                      >
                        {tag}
                      </button>
                    ))}
                    {activeTags.length > 0 && (
                      <button className="text-xs text-red-400 underline ml-2 hover:text-red-300 font-medium flex-shrink-0" onClick={() => {
                        setActiveTags([]);
                        isLoadingMoreRef.current = false;
                        setProblems([]);
                        if (currentPage !== 1) setCurrentPage(1);
                      }}>Clear All</button>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 order-2 justify-between">
                  {/* Left side - Search and Filter Toggle */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <form onSubmit={handleSearch} className="w-full sm:w-auto">
                      <Input
                        search
                        type="text"
                        placeholder="Search by number or name..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className="min-w-[250px]"
                      />
                    </form>

                    {/* Mobile Filter Toggle */}
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className="lg:hidden px-4 py-2 rounded-md bg-background-card border border-border-primary text-text-primary text-sm font-medium flex items-center gap-2 hover:bg-background-hover transition-colors"
                    >
                      <FaFilter />
                      Filters {(difficultyFilter !== 'all' || categoryFilter !== 'all' || activeTags.length > 0) && `(${[difficultyFilter !== 'all', categoryFilter !== 'all', activeTags.length > 0].filter(Boolean).length})`}
                    </button>

                    {/* Active Filters Summary */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {difficultyFilter !== 'all' && (
                        <span className="px-2 py-1 rounded-full text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 flex items-center gap-1">
                          {difficultyFilter}
                          <button onClick={() => {
                            setDifficultyFilter('all');
                            isLoadingMoreRef.current = false;
                            setProblems([]);
                            if (currentPage !== 1) setCurrentPage(1);
                          }} className="hover:text-red-500">
                            <FaTimes size={10} />
                          </button>
                        </span>
                      )}
                      {categoryFilter !== 'all' && (
                        <span className="px-2 py-1 rounded-full text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 flex items-center gap-1">
                          {categoryFilter}
                          <button onClick={() => {
                            setCategoryFilter('all');
                            isLoadingMoreRef.current = false;
                            setProblems([]);
                            if (currentPage !== 1) setCurrentPage(1);
                          }} className="hover:text-red-500">
                            <FaTimes size={10} />
                          </button>
                        </span>
                      )}
                      {sortOrder !== 'question-asc' && (
                        <span className="px-2 py-1 rounded-full text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 flex items-center gap-1">
                          <FaSort size={10} />
                          {sortOrder.replace('-', ' ')}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Mobile Filters Panel */}
                  {showFilters && (
                    <div className="lg:hidden fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
                      <div className="bg-background-card rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[80vh] overflow-y-auto shadow-2xl">
                        <div className="sticky top-0 bg-background-card border-b border-border-primary p-4 flex items-center justify-between">
                          <h3 className="text-lg font-bold">Filters</h3>
                          <button onClick={() => setShowFilters(false)} className="p-2 hover:bg-background-hover rounded-lg">
                            <FaTimes />
                          </button>
                        </div>
                        <div className="p-4 space-y-4">
                          {/* Difficulty */}
                          <div>
                            <label className="text-sm font-medium mb-2 block">Difficulty</label>
                            <select
                              value={difficultyFilter}
                              onChange={(e) => setDifficultyFilter(e.target.value)}
                              className="w-full px-3 py-2 rounded-md bg-background-secondary border border-border-primary text-text-primary focus:ring-2 focus:ring-button-primary-bg focus:outline-none"
                            >
                              <option value="all">All Difficulties</option>
                              <option value="easy">Easy</option>
                              <option value="medium">Medium</option>
                              <option value="hard">Hard</option>
                            </select>
                          </div>

                          {/* Category */}
                          <div>
                            <label className="text-sm font-medium mb-2 block">Category</label>
                            <select
                              value={categoryFilter}
                              onChange={(e) => setCategoryFilter(e.target.value)}
                              className="w-full px-3 py-2 rounded-md bg-background-secondary border border-border-primary text-text-primary focus:ring-2 focus:ring-button-primary-bg focus:outline-none"
                            >
                              <option value="all">All Categories</option>
                              <option value="array">Array</option>
                              <option value="string">String</option>
                              <option value="tree">Tree</option>
                              <option value="graph">Graph</option>
                              <option value="dp">Dynamic Programming</option>
                              <option value="math">Math</option>
                            </select>
                          </div>

                          {/* Sort */}
                          <div>
                            <label className="text-sm font-medium mb-2 block">Sort By</label>
                            <select
                              value={sortOrder}
                              onChange={(e) => setSortOrder(e.target.value)}
                              className="w-full px-3 py-2 rounded-md bg-background-secondary border border-border-primary text-text-primary focus:ring-2 focus:ring-button-primary-bg focus:outline-none"
                            >
                              <option value="question-asc">Question Number: 1 → N</option>
                              <option value="question-desc">Question Number: N → 1</option>
                              <option value="newest">Newest First</option>
                              <option value="oldest">Oldest First</option>
                              <option value="difficulty-asc">Difficulty: Easy → Hard</option>
                              <option value="difficulty-desc">Difficulty: Hard → Easy</option>
                              <option value="acceptance-asc">Acceptance: Low → High</option>
                              <option value="acceptance-desc">Acceptance: High → Low</option>
                            </select>
                          </div>

                          {/* Actions */}
                          <div className="flex gap-2 pt-4">
                            <button
                              onClick={() => {
                                setDifficultyFilter('all');
                                setCategoryFilter('all');
                                setSortOrder('question-asc');
                                setActiveTags([]);
                              }}
                              className="flex-1 px-4 py-2 rounded-lg border border-border-primary text-text-secondary hover:bg-background-hover transition-colors"
                            >
                              Clear All
                            </button>
                            <button
                              onClick={() => setShowFilters(false)}
                              className="flex-1 px-4 py-2 rounded-lg bg-button-primary-bg text-button-primary-text hover:bg-button-primary-hover transition-colors"
                            >
                              Apply
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Right side - Random buttons */}
                  <div className="flex items-center gap-2 flex-wrap ml-auto">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleRandomProblem}
                      title="Random problem from current list"
                    >
                      <FaRandom className="inline mr-1" />
                      Random Problem
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={activeTags.length === 0}
                      onClick={async () => {
                        try {
                          const response = await api.get('/api/problems', { params: { limit: 100, tags: activeTags.join(',') } });
                          const pool = (response.data?.problems || []).filter(p => p._id);
                          if (pool.length === 0) return;
                          const random = pool[Math.floor(Math.random() * pool.length)];
                          navigate(`/problems/${random._id}`);
                        } catch { }
                      }}
                      title="Random problem by selected tags"
                    >
                      🎲 Random by Tags
                    </Button>
                    <div className="flex items-center gap-2 bg-background-tertiary dark:bg-background-secondary rounded-lg p-2">
                      <select
                        className="bg-transparent border-none text-sm font-medium text-text-primary focus:outline-none cursor-pointer"
                        value={randomDifficulty}
                        onChange={(e) => setRandomDifficulty(e.target.value)}
                        title="Select difficulty"
                      >
                        <option value="any">Any Difficulty</option>
                        <option value="easy">Easy</option>
                        <option value="medium">Medium</option>
                        <option value="hard">Hard</option>
                      </select>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={async () => {
                          try {
                            const params = { limit: 100 };
                            if (randomDifficulty !== 'any') params.difficulty = randomDifficulty;
                            const response = await api.get('/api/problems', { params });
                            const pool = (response.data?.problems || []).filter(p => p._id);
                            if (pool.length === 0) return;
                            const random = pool[Math.floor(Math.random() * pool.length)];
                            navigate(`/problems/${random._id}`);
                          } catch { }
                        }}
                        title="Pick a random problem"
                      >
                        Go →
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Problems list */}
              <div className="card overflow-hidden">
                {/* Table header */}
                <div className="grid grid-cols-12 gap-4 p-4 border-b border-border-primary font-medium text-text-secondary flex-shrink-0 bg-background-primary">
                  <div className="col-span-1 text-center">#</div>
                  <div className="col-span-5">Title</div>
                  <div className="col-span-2 text-center">Difficulty</div>
                  <div className="col-span-2 text-center">Acceptance</div>
                  <div className="col-span-2 text-center">Actions</div>
                </div>

                {/* Table body */}
                {loading ? (
                  <Suspense fallback={<div className="p-8 text-center"><div className="animate-spin rounded-full size-8 border-y-2 border-button-primary-bg mx-auto"></div></div>}>
                    <ProblemListSkeleton />
                  </Suspense>
                ) : error ? (
                  <div className="p-8 text-center text-text-danger">
                    <p>{error}</p>
                    <button
                      className="mt-4 btn btn-secondary"
                      onClick={() => window.location.reload()}
                    >
                      Retry
                    </button>
                  </div>
                ) : problems.length === 0 ? (
                  <div className="p-8 text-center text-text-tertiary">
                    <p>No problems found matching your criteria</p>
                  </div>
                ) : (
                  <div>
                    {problems.map((problem, index) => (
                      <ProblemRow
                        key={problem._id}
                        problem={problem}
                        index={index}
                        onNavigate={navigateToProblem}
                        onPreview={handlePreview}
                        onTagClick={handleTagClick}
                        getDifficultyBadge={getDifficultyBadge}
                        favoriteListId={favoriteListId}
                        onToggleFavorite={handleToggleFavorite}
                      />
                    ))}
                  </div>
                )}

                {/* Compact Loading more indicator */}
                {loadingMore && (
                  <div className="p-4 text-center bg-background-primary border-t border-border-primary">
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full size-4 border-2 border-button-primary-bg border-t-transparent"></div>
                      <span className="text-sm text-text-secondary">Loading more...</span>
                    </div>
                  </div>
                )}

                {/* End of list message */}
                {!loading && !loadingMore && !hasMore && problems.length > 0 && (
                  <div className="p-8 text-center text-text-secondary bg-background-secondary">
                    <p>✅ You've reached the end of the list ({problems.length} problems total)</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {previewProblem && (
        <ProblemPreviewModal
          problem={previewProblem}
          onClose={() => setPreviewProblem(null)}
        />
      )}

    </div>
  );
};

export default Problems;